/**
 * Stellaris Footwear - Glassmorphism Dynamic Tinting System
 * Detects product colors and applies dynamic tints to glass elements
 */

(function() {
  'use strict';

  // Color mapping for product tints
  const colorMap = {
    'mars-red': { r: 220, g: 38, b: 38 },
    'galaxy-blue': { r: 59, g: 130, b: 246 },
    'cosmic-purple': { r: 147, g: 51, b: 234 },
    'stellar-gold': { r: 234, g: 179, b: 8 },
    'nebula-pink': { r: 236, g: 72, b: 153 },
    'void-black': { r: 0, g: 0, b: 0 },
    'starlight-white': { r: 255, g: 255, b: 255 },
    'aurora-green': { r: 34, g: 197, b: 94 },
    'solar-orange': { r: 249, g: 115, b: 22 },
    'lunar-silver': { r: 148, g: 163, b: 184 }
  };

  /**
   * Extract color from product title, variant, or image
   */
  function extractProductColor(productElement) {
    // Check data attribute first
    const dataTint = productElement.getAttribute('data-tint');
    if (dataTint && colorMap[dataTint]) {
      return dataTint;
    }

    // Check product title for color keywords
    const titleElement = productElement.querySelector('.card__heading, .product-title, h2, h3');
    if (titleElement) {
      const title = titleElement.textContent.toLowerCase();
      for (const [colorName, colorValue] of Object.entries(colorMap)) {
        if (title.includes(colorName.split('-')[0])) {
          return colorName;
        }
      }
    }

    // Check variant options
    const variantElement = productElement.querySelector('[data-variant-color]');
    if (variantElement) {
      const variantColor = variantElement.getAttribute('data-variant-color');
      if (colorMap[variantColor]) {
        return variantColor;
      }
    }

    // Try to extract color from image (basic color detection)
    const imageElement = productElement.querySelector('img');
    if (imageElement && imageElement.complete) {
      const color = extractDominantColor(imageElement);
      if (color) {
        return findClosestColor(color);
      }
    }

    return null;
  }

  /**
   * Extract dominant color from image (simplified version)
   */
  function extractDominantColor(img) {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 100;
      canvas.height = 100;
      ctx.drawImage(img, 0, 0, 100, 100);
      
      const imageData = ctx.getImageData(0, 0, 100, 100);
      const data = imageData.data;
      
      let r = 0, g = 0, b = 0, count = 0;
      for (let i = 0; i < data.length; i += 16) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count++;
      }
      
      return {
        r: Math.round(r / count),
        g: Math.round(g / count),
        b: Math.round(b / count)
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Find closest color match
   */
  function findClosestColor(targetColor) {
    let closestColor = null;
    let minDistance = Infinity;

    for (const [colorName, colorValue] of Object.entries(colorMap)) {
      const distance = Math.sqrt(
        Math.pow(targetColor.r - colorValue.r, 2) +
        Math.pow(targetColor.g - colorValue.g, 2) +
        Math.pow(targetColor.b - colorValue.b, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestColor = colorName;
      }
    }

    return closestColor;
  }

  /**
   * Apply tint to product card
   */
  function applyTintToCard(card, colorName) {
    if (!colorName || !colorMap[colorName]) return;

    card.setAttribute('data-tint', colorName);
    card.setAttribute('data-tint-color', colorName);
    
    const color = colorMap[colorName];
    const root = document.documentElement;
    root.style.setProperty('--tint-color', `rgba(${color.r}, ${color.g}, ${color.b}, var(--tint-intensity))`);
  }

  /**
   * Apply global tint when viewing a product
   */
  function applyGlobalTint(colorName) {
    if (!colorName || !colorMap[colorName]) {
      document.body.removeAttribute('data-active-tint');
      return;
    }

    const color = colorMap[colorName];
    document.body.setAttribute('data-active-tint', colorName);
    
    const root = document.documentElement;
    root.style.setProperty('--tint-color', `rgba(${color.r}, ${color.g}, ${color.b}, var(--tint-intensity))`);
  }

  /**
   * Initialize tinting for all product cards
   */
  function initializeProductTints() {
    const productCards = document.querySelectorAll('.card, .product-card, [data-product-id]');
    
    productCards.forEach(card => {
      const colorName = extractProductColor(card);
      if (colorName) {
        applyTintToCard(card, colorName);
      }
    });
  }

  /**
   * Handle product page view
   */
  function handleProductPageView() {
    const productCard = document.querySelector('.product, [data-product-id]');
    if (productCard) {
      const colorName = extractProductColor(productCard);
      if (colorName) {
        applyGlobalTint(colorName);
      }
    }
  }

  /**
   * Add liquid distortion on hover
   */
  function addLiquidDistortion() {
    const cards = document.querySelectorAll('.card');
    
    cards.forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const deltaX = (x - centerX) / centerX;
        const deltaY = (y - centerY) / centerY;
        
        const distortion = Math.sqrt(deltaX * deltaX + deltaY * deltaY) * 5;
        card.style.setProperty('--liquid-distortion', `${distortion}px`);
      });

      card.addEventListener('mouseleave', () => {
        card.style.setProperty('--liquid-distortion', '0px');
      });
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initializeProductTints();
        handleProductPageView();
        addLiquidDistortion();
      });
    } else {
      initializeProductTints();
      handleProductPageView();
      addLiquidDistortion();
    }

    // Re-initialize when new products are loaded (AJAX)
    const observer = new MutationObserver(() => {
      initializeProductTints();
      addLiquidDistortion();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Start initialization
  init();

  // Export for external use
  window.StellarisGlassmorphism = {
    applyTint: applyTintToCard,
    applyGlobalTint: applyGlobalTint,
    extractColor: extractProductColor
  };

})();

